import { useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { Crown, KeyRound, ShieldAlert, AlertTriangle } from "lucide-react";
import api, { clearSession, setSession, errMsg } from "@/lib/api";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";

export default function SuperAdminLogin() {
    const nav = useNavigate();
    const location = useLocation();
    const redirectTo = new URLSearchParams(location.search).get("next") || "/super-admin";
    const [form, setForm] = useState({ email: "", password: "", totp_code: "" });
    const [busy, setBusy] = useState(false);
    const [needTfa, setNeedTfa] = useState(false);
    const [wrongPortalRole, setWrongPortalRole] = useState(null);

    const submit = async (e) => {
        e.preventDefault();
        setBusy(true);
        setWrongPortalRole(null);
        try {
            const { data } = await api.post("/auth/login", form);
            if (data.two_factor_required && !data.access_token) {
                setNeedTfa(true);
                toast.info("Enter your 6-digit TOTP code to continue.");
                return;
            }
            if (data.user.role !== "super_admin") {
                clearSession();
                setWrongPortalRole(data.user.role);
                toast.error("Super Admin credentials required.");
                return;
            }
            setSession(data.access_token, data.user);
            toast.success(`Welcome ${data.user.full_name}`);
            nav(redirectTo);
        } catch (err) {
            toast.error(errMsg(err, "Login failed"));
        } finally {
            setBusy(false);
        }
    };

    return (
        <div
            className="min-h-screen text-white flex flex-col relative overflow-hidden"
            style={{ background: "linear-gradient(180deg, #050914 0%, #0B1633 55%, #050914 100%)" }}
            data-testid="super-admin-login-page"
        >
            {/* Elevated aesthetic: gold shimmer, muted grid, spotlight vignettes */}
            <div className="fixed inset-0 pointer-events-none"
                 style={{
                     backgroundImage:
                         "radial-gradient(1400px 600px at 50% -10%, rgba(212,175,55,0.10), transparent 65%)," +
                         "radial-gradient(600px 300px at 15% 100%, rgba(58,128,247,0.12), transparent 60%)," +
                         "radial-gradient(600px 300px at 85% 100%, rgba(212,175,55,0.06), transparent 60%)",
                 }} />
            <div className="fixed inset-0 pointer-events-none opacity-[0.06]"
                 style={{
                     backgroundImage:
                         "linear-gradient(transparent 97%, rgba(255,255,255,0.9) 97%)," +
                         "linear-gradient(90deg, transparent 97%, rgba(255,255,255,0.9) 97%)",
                     backgroundSize: "56px 56px, 56px 56px",
                 }} />

            <header className="relative z-10 px-8 py-6 flex items-center justify-between">
                <div className="inline-flex items-center gap-3">
                    <img src="/brand-icon.png" alt="Doctor Digital ID" className="h-11 w-11 object-contain" />
                    <div className="leading-tight">
                        <div className="text-[11px] tracking-[0.32em] uppercase text-white/40">DOCTOR DIGITAL ID</div>
                        <div className="text-lg font-semibold tracking-wide">Super Admin Console</div>
                    </div>
                </div>
                <a href="/admin/login" className="text-[13px] text-white/50 hover:text-white transition-colors" data-testid="super-login-goto-admin">
                    ← Admin sign-in
                </a>
            </header>

            <main className="relative z-10 flex-1 flex items-center justify-center px-6 pb-16">
                <div className="w-full max-w-md">
                    <div className="text-center mb-8">
                        <div className="inline-flex items-center gap-2 text-[11px] tracking-[0.32em] uppercase mb-4"
                             style={{ color: "#D4AF37" }}>
                            <ShieldAlert size={13} /> highest privilege
                        </div>
                        <h1 className="text-4xl font-bold tracking-tight">
                            Super Admin
                        </h1>
                        <p className="mt-3 text-white/50 text-sm">
                            Platform-wide packages, features, admins, audit &amp; system settings.
                        </p>
                    </div>

                    <form
                        onSubmit={submit}
                        className="bg-white/[0.04] backdrop-blur-2xl rounded-2xl p-8 shadow-2xl relative"
                        style={{ boxShadow: "0 30px 80px -20px rgba(0,0,0,0.6), inset 0 0 0 1px rgba(212,175,55,0.15)" }}
                        data-testid="super-admin-login-form"
                    >
                        {wrongPortalRole && (
                            <div
                                className="mb-5 flex items-start gap-3 rounded-xl p-3 text-[13px]"
                                style={{ background: "rgba(220,38,38,0.10)", boxShadow: "inset 0 0 0 1px rgba(220,38,38,0.35)" }}
                                data-testid="super-admin-login-wrong-role"
                            >
                                <AlertTriangle size={16} className="text-[#F87171] shrink-0 mt-0.5" />
                                <div className="text-white/80">
                                    You signed in as <b>{wrongPortalRole}</b>. Only <b>super_admin</b> can access this console.{" "}
                                    {wrongPortalRole === "admin" ? (
                                        <a href="/admin/login" className="font-semibold hover:underline" style={{ color: "#D4AF37" }}>
                                            Use the admin sign-in →
                                        </a>
                                    ) : (
                                        <a href="/login" className="font-semibold hover:underline" style={{ color: "#D4AF37" }}>
                                            Use the doctor sign-in →
                                        </a>
                                    )}
                                </div>
                            </div>
                        )}

                        <div className="space-y-5">
                            <div>
                                <Label htmlFor="sa-email" className="text-white/50 text-[11px] tracking-[0.24em] uppercase">Super admin email</Label>
                                <Input
                                    id="sa-email"
                                    type="email"
                                    autoComplete="username"
                                    value={form.email}
                                    onChange={(e) => setForm({ ...form, email: e.target.value })}
                                    className="mt-2 bg-white/8 border-white/8 text-white placeholder-white/25 h-11 focus:bg-white/12"
                                    data-testid="super-admin-login-email"
                                    required
                                />
                            </div>
                            <div>
                                <Label htmlFor="sa-pw" className="text-white/50 text-[11px] tracking-[0.24em] uppercase">Password</Label>
                                <Input
                                    id="sa-pw"
                                    type="password"
                                    autoComplete="current-password"
                                    value={form.password}
                                    onChange={(e) => setForm({ ...form, password: e.target.value })}
                                    className="mt-2 bg-white/8 border-white/8 text-white placeholder-white/25 h-11 focus:bg-white/12"
                                    data-testid="super-admin-login-password"
                                    required
                                />
                            </div>
                            {needTfa && (
                                <div>
                                    <Label htmlFor="sa-totp" className="text-white/50 text-[11px] tracking-[0.24em] uppercase inline-flex items-center gap-2">
                                        <KeyRound size={11} /> Two-factor code
                                    </Label>
                                    <Input
                                        id="sa-totp"
                                        type="text"
                                        inputMode="numeric"
                                        maxLength={6}
                                        pattern="\d{6}"
                                        placeholder="123 456"
                                        value={form.totp_code}
                                        onChange={(e) => setForm({ ...form, totp_code: e.target.value })}
                                        className="mt-2 bg-white/8 border-white/8 text-white placeholder-white/25 h-11 focus:bg-white/12 tracking-[0.32em] text-center font-mono"
                                        data-testid="super-admin-login-totp"
                                        required
                                    />
                                </div>
                            )}
                        </div>

                        <button
                            type="submit"
                            disabled={busy}
                            className="mt-7 w-full h-12 rounded-xl font-semibold text-sm disabled:opacity-60 disabled:cursor-not-allowed inline-flex items-center justify-center gap-2 transition-all shadow-lg"
                            style={{
                                background: busy
                                    ? "rgba(212,175,55,0.4)"
                                    : "linear-gradient(135deg, #D4AF37 0%, #C0972B 100%)",
                                color: "#050914",
                                boxShadow: "0 12px 30px -8px rgba(212,175,55,0.35)",
                            }}
                            data-testid="super-admin-login-submit"
                        >
                            {busy ? "Verifying…" : (needTfa ? "Verify & enter console" : "Enter console")}
                        </button>
                    </form>

                    <div className="mt-6 text-center text-[11px] text-white/35 tracking-wide">
                        Every action performed in this console is audit-logged.
                    </div>
                </div>
            </main>
        </div>
    );
}
